import {
  Conversation,
  TypingEvent,
  StaffPresenceEvent,
  NewMessageEvent,
  ConversationUpdateEvent,
  SentimentAlertEvent,
} from '../../types';

// Event type constants
export const SOCKET_EVENTS = {
  // Connection
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',

  // Conversations
  CONVERSATION_UPDATED: 'conversation:updated',
  CONVERSATION_NEW: 'conversation:new',
  CONVERSATION_ASSIGNED: 'conversation:assigned',
  CONVERSATION_RESOLVED: 'conversation:resolved',

  // Messages
  MESSAGE_NEW: 'message:new',
  MESSAGE_SENT: 'message:sent',
  MESSAGE_DELIVERED: 'message:delivered',
  MESSAGE_READ: 'message:read',
  MESSAGE_FAILED: 'message:failed',
  MESSAGE_EDITED: 'message:edited',
  MESSAGE_DELETED: 'message:deleted',

  // Agent
  AGENT_ERROR: 'agent:error',

  // Copilot (private staff ↔ AI side-conversation; default namespace only)
  COPILOT_MESSAGE: 'copilot:message',

  // Team group chat (internal staff channel; default namespace only)
  TEAM_MESSAGE_NEW: 'team:message',
  SUBSCRIBE_TEAM_CHANNEL: 'subscribe:team-channel',
  UNSUBSCRIBE_TEAM_CHANNEL: 'unsubscribe:team-channel',

  // Intelligence
  SENTIMENT_ALERT: 'intelligence:sentiment-alert',

  // Typing indicators
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  STAFF_TYPING: 'staff:typing',
  STAFF_STOP_TYPING: 'staff:stop-typing',

  // Staff presence
  STAFF_ONLINE: 'staff:online',
  STAFF_OFFLINE: 'staff:offline',
  STAFF_PRESENCE: 'staff:presence',

  // Voice calls (Phase 1 contract)
  CALL_INCOMING: 'call:incoming',
  CALL_ANSWERED: 'call:answered',
  CALL_STATUS: 'call:status',
  CALL_RECORDING_READY: 'call:recording-ready',
  // V2 AI voice agent — emitted when the agent ends a call and
  // requests a staff callback. Cold transfer; staff hit 'Call back
  // now' on the toast to immediately dial the customer via Twilio
  // outbound.
  VOICE_AI_ESCALATION: 'voice:ai_escalation',

  // Subscriptions
  SUBSCRIBE_CONVERSATION: 'subscribe:conversation',
  UNSUBSCRIBE_CONVERSATION: 'unsubscribe:conversation',

  // Customer feedback (CSAT)
  FEEDBACK_RECORDED: 'feedback:recorded',
} as const;

// Voice call payload types (locked Phase 1 contract — match backend exactly)
export type CallTransport = 'webrtc' | 'twilio';

export type CallStatus =
  | 'ringing'
  | 'answered'
  | 'missed'
  | 'declined'
  | 'completed'
  | 'failed'
  | 'on_hold'
  | 'transferring'
  | 'ended';

export interface IncomingCallPayload {
  id: string;
  conversationId: string;
  customerName: string;
  customerPhone: string;
  transport: CallTransport;
  ringingAt: string; // ISO 8601
  offerSdp: string;
}

export interface CallAnsweredPayload {
  callId: string;
  staffId: string;
}

export interface CallStatusPayload {
  callId: string;
  status: CallStatus;
}

export interface CallRecordingReadyPayload {
  callId: string;
  recordingUrl: string;
  transcript: string | null;
}

export interface AiEscalationPayload {
  conversationId: string;
  customerName: string;
  customerPhone: string;
  reason: string;
}

// Event payload types
export interface SocketEventMap {
  [SOCKET_EVENTS.CONNECT]: void;
  [SOCKET_EVENTS.DISCONNECT]: string;
  [SOCKET_EVENTS.CONNECT_ERROR]: Error;

  [SOCKET_EVENTS.CONVERSATION_UPDATED]: ConversationUpdateEvent;
  [SOCKET_EVENTS.CONVERSATION_NEW]: Conversation;
  [SOCKET_EVENTS.CONVERSATION_ASSIGNED]: { conversationId: string; staffId: string };
  [SOCKET_EVENTS.CONVERSATION_RESOLVED]: { conversationId: string };

  [SOCKET_EVENTS.MESSAGE_NEW]: NewMessageEvent;
  [SOCKET_EVENTS.MESSAGE_SENT]: { messageId: string };
  [SOCKET_EVENTS.MESSAGE_DELIVERED]: { messageId: string };
  [SOCKET_EVENTS.MESSAGE_READ]: { messageId: string; conversationId: string };
  [SOCKET_EVENTS.MESSAGE_FAILED]: { messageId: string; error: string };
  [SOCKET_EVENTS.MESSAGE_EDITED]: MessageEditedPayload;
  [SOCKET_EVENTS.MESSAGE_DELETED]: MessageDeletedPayload;

  [SOCKET_EVENTS.AGENT_ERROR]: { conversationId: string; error: string; timestamp: string };

  [SOCKET_EVENTS.COPILOT_MESSAGE]: CopilotMessageEvent;

  [SOCKET_EVENTS.TEAM_MESSAGE_NEW]: TeamMessageEvent;
  [SOCKET_EVENTS.SUBSCRIBE_TEAM_CHANNEL]: string;
  [SOCKET_EVENTS.UNSUBSCRIBE_TEAM_CHANNEL]: string;

  [SOCKET_EVENTS.SENTIMENT_ALERT]: SentimentAlertEvent;

  [SOCKET_EVENTS.TYPING_START]: string;
  [SOCKET_EVENTS.TYPING_STOP]: string;
  [SOCKET_EVENTS.STAFF_TYPING]: TypingEvent;
  [SOCKET_EVENTS.STAFF_STOP_TYPING]: TypingEvent;

  [SOCKET_EVENTS.STAFF_ONLINE]: StaffPresenceEvent;
  [SOCKET_EVENTS.STAFF_OFFLINE]: StaffPresenceEvent;
  [SOCKET_EVENTS.STAFF_PRESENCE]: StaffPresenceEvent[];

  [SOCKET_EVENTS.CALL_INCOMING]: IncomingCallPayload;
  [SOCKET_EVENTS.CALL_ANSWERED]: CallAnsweredPayload;
  [SOCKET_EVENTS.CALL_STATUS]: CallStatusPayload;
  [SOCKET_EVENTS.CALL_RECORDING_READY]: CallRecordingReadyPayload;
  [SOCKET_EVENTS.VOICE_AI_ESCALATION]: AiEscalationPayload;

  [SOCKET_EVENTS.SUBSCRIBE_CONVERSATION]: string;
  [SOCKET_EVENTS.UNSUBSCRIBE_CONVERSATION]: string;

  [SOCKET_EVENTS.FEEDBACK_RECORDED]: FeedbackRecordedPayload;
}

export interface FeedbackRecordedPayload {
  conversationId: string;
  score: number;
  respondedAt: string;
}

export interface CopilotMessageEvent {
  conversationId: string;
  message: import('../../types').CopilotMessage;
}

export interface TeamMessage {
  id: string;
  channelId: string;
  sender: 'staff' | 'agent' | 'system';
  senderId: string | null;
  senderName: string | null;
  content: string;
  mediaUrl?: string | null;
  mediaType?: 'image' | 'video' | 'audio' | 'document' | null;
  fileName?: string | null;
  messageMetadata: Record<string, unknown>;
  createdAt: string | null;
}

export interface TeamMessageEvent {
  channelId: string;
  message: TeamMessage;
}

export interface MessageEditedPayload {
  conversationId: string;
  messageId: string;
  content: string;
  editedAt: string;
  editedBy: string;
  channelPropagated: boolean;
}

export interface MessageDeletedPayload {
  conversationId: string;
  messageId: string;
  deletedAt: string;
  deletedBy: string;
  channelPropagated: boolean;
}
